/* eslint-disable @typescript-eslint/no-unused-vars */

import { useNavigate, useParams } from "react-router-dom";
import AddRouteMap from "./_components/AddRouteMap";
import { useFetchRouteDetailsData } from "../../apis/routeApis";
import { useEffect, useState } from "react";
import { useAssignRoute, useFetchDriversAllData } from "../../apis/driverApis";
import toast from "react-hot-toast";
import useDebounce from "../../utilities/useDebounce";
import MessageModal from "../../components/MessageModal";

interface Driver {
  id: string;
  name: string;
  routeCodes?: string[];
  profileImage?: string;
}

export default function RouteAssign() {
  const { id } = useParams<{ id: string }>();
  const [editMode, setEditMode] = useState(false);
  const [assigningDriverId, setAssigningDriverId] = useState<string | null>(
    null
  );
  const [searchFor, setSearchFor] = useState("");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState<Driver | null>(null);

  const {
    data: routeDetails,
    isPending: isRouteDetailsPending,
    refetch: fetchRouteDetails,
  } = useFetchRouteDetailsData(id ?? "");

  const { data: drivers, refetch: fetchDrivers } =
    useFetchDriversAllData(searchFor);

  const { mutate: assignRoute } = useAssignRoute();

  useEffect(() => {
    if (id) {
      fetchRouteDetails();
    }
  }, [id, fetchRouteDetails]);

  const debouncedSearchFor = useDebounce(searchFor, 550);

  useEffect(() => {
    fetchDrivers();
  }, [debouncedSearchFor, fetchDrivers]);

  const handleAssignRoute = async (
    driver: Driver,
    action: "Assign" | "Unassign"
  ) => {
    setAssigningDriverId(driver.id);
    try {
      const currentRouteCodes = driver.routeCodes || [];
      const currentRoute = routeDetails?.routeCode || "";

      const data = {
        id: driver.id,
        routeCodes:
          action === "Assign"
            ? [...currentRouteCodes, currentRoute]
            : currentRouteCodes.filter((route) => route !== currentRoute),
      };

      assignRoute(data, {
        onSuccess: () => {
          toast.success(
            `Driver ${
              action === "Assign" ? "assigned to" : "unassigned from"
            } route successfully`
          );
          fetchDrivers();
          setShowConfirmation(false);
        },
        onError: (error: any) => {
          console.error("Assignment error:", error);
          toast.error(
            error?.response?.data?.message || "Error in assigning route"
          );
        },
      });
    } catch (error) {
      console.error("Unexpected error:", error);
      toast.error("An unexpected error occurred");
    } finally {
      setAssigningDriverId(null);
    }
  };

  return (
    <div className="px-4">
      <AddRouteMap
        type={"update"}
        routeDetails={routeDetails}
        editMode={editMode}
        onEditMode={() => setEditMode(!editMode)}
      />
      <MessageModal
        open={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        title="Driver Already Assigned"
        subtitle={
          <div className="text-left space-y-2">
            <p className="text-gray-600 text-center">
              {selectedDriver?.name} is currently assigned to{" "}
              {selectedDriver?.routeCodes?.join(",") || 0} route(s).
            </p>
          </div>
        }
        img="/img/warning.png"
        actionButtonText="Confirm"
        closeButtonText="Cancel"
        actionButtonOnClick={() =>
          selectedDriver && handleAssignRoute(selectedDriver, "Assign")
        }
        isPending={assigningDriverId === selectedDriver?.id}
      />
    </div>
  );
}